import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { gestApi, useGestAuth } from "./GestAuthContext";

const SUGGESTED = [
  "Fammi il punto di tutti i cantieri.",
  "Quali cantieri sono a rischio?",
  "Quali fornitori hanno generato più costi?",
  "Quali cantieri hanno la produzione avanti rispetto alla posa?",
];

/**
 * Floating AI analytics assistant.
 * Rendered by GestLayout, visible only to admin/ufficio (RBAC also enforced on the backend endpoint).
 */
export default function GestAiAssistant() {
  const { user } = useGestAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // { role: 'user'|'assistant', text, at }
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const canUse = user && (user.role === "admin" || user.role === "ufficio");

  useEffect(() => {
    if (open && inputRef.current) setTimeout(() => inputRef.current?.focus(), 150);
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  if (!canUse) return null;

  const send = async (question) => {
    const q = (question || input).trim();
    if (!q || busy) return;
    setInput("");
    const now = new Date().toISOString();
    setMessages((m) => [...m, { id: crypto.randomUUID(), role: "user", text: q, at: now }]);
    setBusy(true);
    try {
      const { data } = await gestApi.post("/chat", { question: q, session_id: sessionId }, { timeout: 60000 });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { id: crypto.randomUUID(), role: "assistant", text: data.answer, at: new Date().toISOString() }]);
    } catch (e) {
      const msg = e.response?.data?.detail || "Assistente temporaneamente non disponibile";
      setMessages((m) => [...m, { id: crypto.randomUUID(), role: "assistant", text: `⚠️ ${msg}`, at: new Date().toISOString(), error: true }]);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const clearChat = () => {
    if (messages.length && !window.confirm("Svuotare la chat? La cronologia viene cancellata solo dallo schermo.")) return;
    setMessages([]);
    setSessionId(null);
  };

  return (
    <>
      {/* Floating action button */}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="gest-ai-fab"
        aria-label={open ? "Chiudi Pedro" : "Apri Pedro — Assistente HGW"}
        className={`fixed z-40 bottom-24 right-4 md:bottom-7 md:right-7 w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all ${
          open
            ? "bg-white text-[#1D3F94] border border-[#1D3F94]/20"
            : "bg-[#1D3F94] text-white hover:bg-[#173277] hover:scale-105"
        }`}
        style={{ boxShadow: open ? "0 8px 24px rgba(0,0,0,0.12)" : "0 12px 32px rgba(29,63,148,0.4)" }}
      >
        {open ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3l1.9 4.2L18 9l-4.1 1.8L12 15l-1.9-4.2L6 9l4.1-1.8L12 3z"/>
            <path d="M19 14l0.9 2 2 0.9-2 0.9-0.9 2-0.9-2-2-0.9 2-0.9 0.9-2z"/>
          </svg>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            key="ai-panel"
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            data-testid="gest-ai-panel"
            className="fixed z-40 bottom-40 md:bottom-28 right-4 md:right-7 w-[calc(100vw-2rem)] md:w-[440px] h-[calc(100vh-13rem)] md:h-[600px] max-h-[720px] bg-white rounded-2xl shadow-2xl border border-black/[0.04] flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-black/5 flex items-center justify-between bg-[#1D3F94] text-white">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-8 h-8 rounded-full bg-[#1D3F94]/25 flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#a7c8e9" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 3l1.9 4.2L18 9l-4.1 1.8L12 15l-1.9-4.2L6 9l4.1-1.8L12 3z"/>
                    <path d="M19 14l0.9 2 2 0.9-2 0.9-0.9 2-0.9-2-2-0.9 2-0.9 0.9-2z"/>
                  </svg>
                </div>
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold truncate">Pedro</div>
                  <div className="text-[10.5px] text-white/55 truncate">Analytics HGW · dati in tempo reale</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {messages.length > 0 && (
                  <button
                    onClick={clearChat}
                    data-testid="gest-ai-clear"
                    className="text-white/60 hover:text-white text-[11px] px-2 py-1"
                    title="Nuova conversazione"
                  >Nuova</button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="text-white/70 hover:text-white w-7 h-7 flex items-center justify-center"
                  aria-label="Chiudi"
                >×</button>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="gest-ai-messages">
              {messages.length === 0 && (
                <div className="text-center pt-6">
                  <div className="text-[13px] font-semibold text-[#0a1420]">Ciao {user?.name?.split(" ")[0] || ""}, sono Pedro</div>
                  <div className="text-[12px] text-[#6e6e73] mt-1 mb-5 leading-relaxed">
                    Rispondo sui tuoi cantieri usando i dati reali del sistema.<br/>Provami:
                  </div>
                  <div className="space-y-2">
                    {SUGGESTED.map((s) => (
                      <button
                        key={s}
                        onClick={() => send(s)}
                        data-testid={`gest-ai-suggest-${s.slice(0, 12)}`}
                        className="w-full text-left px-3.5 py-2.5 text-[12.5px] text-[#0a1420] bg-[#f5f7fb] hover:bg-[#e9edf3] rounded-xl border border-black/[0.04] transition-colors"
                      >{s}</button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`gest-ai-msg-${m.role}-${i}`}
                    className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-[13px] leading-[1.5] whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-[#1D3F94] text-white rounded-br-sm"
                        : m.error
                          ? "bg-red-50 text-red-900 border border-red-100 rounded-bl-sm"
                          : "bg-[#f5f7fb] text-[#0a1420] rounded-bl-sm"
                    }`}
                  >{m.text}</div>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start">
                  <div className="px-3.5 py-3 rounded-2xl bg-[#f5f7fb] rounded-bl-sm">
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#1D3F94] animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-[#1D3F94] animate-bounce" style={{ animationDelay: "120ms" }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-[#1D3F94] animate-bounce" style={{ animationDelay: "240ms" }} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="p-3 border-t border-black/5 bg-white">
              <form
                onSubmit={(e) => { e.preventDefault(); send(); }}
                className="flex items-center gap-2 bg-[#f5f7fb] rounded-xl px-3 py-2 border border-transparent focus-within:border-[#1D3F94]/30 transition-colors"
              >
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  disabled={busy}
                  placeholder="Chiedi a Pedro sui cantieri…"
                  data-testid="gest-ai-input"
                  maxLength={1000}
                  className="flex-1 bg-transparent outline-none text-[13px] text-[#0a1420] placeholder:text-[#6e6e73] disabled:opacity-60"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || busy}
                  data-testid="gest-ai-send"
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#1D3F94] text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#173277]"
                  aria-label="Invia"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                </button>
              </form>
              <div className="text-[10px] text-[#6e6e73] mt-1.5 text-center">
                Le risposte si basano solo sui dati presenti nel sistema. Nessun dato inventato.
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
